"use client";

import { Quote } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function VictoryClips() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const tiles = [{
    type: "image",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701752/img/esports_tournament_clutch.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701752/img/esports_tournament_clutch.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701752/img/esports_tournament_clutch.png-wvc-sizes",
    alt: "Competitive esports player making a clutch play",
    span: "md:col-span-1 md:row-span-1",
    clip: true
  }, {
    type: "fact",
    stat: "98%",
    caption: "TOURNAMENT WIN RATE IN SEASON FINALS",
    span: "md:col-span-1 md:row-span-1"
  }, {
    type: "feature",
    quote: "Victory isn't just about the final score; it's about the split-second decisions that define a legacy. Pixel Force is where legends are forged in the heat of the digital arena.",
    author: "Jax 'Neon' Thorne",
    role: "Team Captain, Pixel Force",
    span: "md:col-span-2 md:row-span-2"
  }, {
    type: "image",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701752/img/pro_gaming_setup_neon.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701752/img/pro_gaming_setup_neon.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701752/img/pro_gaming_setup_neon.png-wvc-sizes",
    alt: "Professional gaming setup with neon cyan lighting",
    span: "md:col-span-2 md:row-span-1",
    clip: false
  }, {
    type: "fact",
    stat: "15",
    caption: "ACTIVE PRO PLAYERS IN OUR ROSTER",
    span: "md:col-span-1 md:row-span-1"
  }, {
    type: "image",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701752/img/esports_trophy_victory.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701752/img/esports_trophy_victory.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701752/img/esports_trophy_victory.webp-wvc-sizes",
    alt: "Championship trophy glowing in neon light",
    span: "md:col-span-1 md:row-span-1",
    clip: true
  }, {
    type: "image",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701752/img/team_jersey_merch.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701752/img/team_jersey_merch.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701752/img/team_jersey_merch.png-wvc-sizes",
    alt: "Official Pixel Force team jersey",
    span: "md:col-span-1 md:row-span-1",
    clip: false
  }, {
    type: "fact",
    stat: "50K",
    caption: "COMMUNITY MEMBERS IN OUR DISCORD",
    span: "md:col-span-1 md:row-span-1"
  }, {
    type: "image",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701752/img/esports_live_stream_action.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701752/img/esports_live_stream_action.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701752/img/esports_live_stream_action.png-wvc-sizes",
    alt: "Action shot from a live esports stream",
    span: "md:col-span-1 md:row-span-1",
    clip: true
  }];
  return <section data-section-id="963" id="victory-clips" className="bg-background text-foreground py-20 md:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div ref={headerRef} className={`mx-auto max-w-3xl text-center transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-mono text-xs tracking-[0.2em] uppercase text-primary">
            Tactical Highlights &amp; Data Logs
          </p>
          <h2 className="mt-5 font-default font-bold text-4xl md:text-[52px] leading-[1.05] tracking-tight text-foreground">
            The{" "}
            <em className="italic font-default text-primary">
              Pixel Force
            </em>{" "}
            Archive
          </h2>
          <p className="mt-6 font-default text-base md:text-lg text-muted-foreground max-w-xl mx-auto">
            Witness the intensity of our latest tournament victories, pro-player highlights, and the pulse of our competitive community.
          </p>
        </div>

        <div ref={gridRef} className={`mt-14 md:mt-20 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 md:auto-rows-[220px] transition-all duration-700 ease-out delay-100 ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          {tiles.map((tile, i) => {
          if (tile.type === "image") {
            return <div key={i} data-index={i} className={`group relative overflow-hidden rounded-lg bg-secondary shadow-md transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${tile.span} ${tile.clip ? "[clip-path:polygon(0_0,calc(100%-32px)_0,100%_32px,100%_100%,0_100%)]" : ""}`}>
                  <img src={tile.src} data-wvc-srcset={tile.srcset} data-wvc-sizes={tile.sizes} alt={tile.alt} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                </div>;
          }
          if (tile.type === "fact") {
            return <div key={i} data-index={i} className={`relative flex flex-col justify-between rounded-lg bg-card text-card-foreground p-6 md:p-7 shadow-md transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${tile.span}`}>
                  <div aria-hidden="true" className="absolute top-5 right-5 h-2.5 w-2.5 rotate-45 bg-primary" />
                  <p className="font-default font-bold text-5xl md:text-6xl leading-none tracking-tight text-primary">
                    {tile.stat}
                  </p>
                  <p className="font-mono text-[11px] tracking-[0.18em] uppercase text-muted-foreground mt-6 leading-relaxed">
                    {tile.caption}
                  </p>
                </div>;
          }
          return <div key={i} data-index={i} className={`relative flex flex-col justify-between rounded-lg p-7 md:p-10 shadow-lg transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl ${tile.span}`} style={{
            backgroundColor: "var(--secondary)"
          }}>
                <div aria-hidden="true" className="absolute top-6 right-6 opacity-30" style={{
              color: "var(--primary)"
            }}>
                  <Quote className="size-10" />
                </div>
                <div className="relative">
                  <p className="font-mono text-[11px] tracking-[0.2em] uppercase" style={{
                color: "var(--primary)"
              }}>
                    Pro Insight
                  </p>
                  <p className="mt-6 font-default font-semibold text-2xl md:text-3xl lg:text-[34px] leading-[1.2] tracking-tight" style={{
                color: "var(--secondary-foreground)"
              }}>
                    &ldquo;{tile.quote}&rdquo;
                  </p>
                </div>
                <div className="relative mt-8">
                  <div className="h-px w-12 mb-4" style={{
                backgroundColor: "var(--primary)"
              }} />
                  <p className="font-default font-bold text-base" style={{
                color: "var(--secondary-foreground)"
              }}>
                    {tile.author}
                  </p>
                  <p className="font-mono text-[11px] tracking-[0.15em] uppercase mt-1" style={{
                color: "var(--primary)",
                opacity: 0.9
              }}>
                    {tile.role}
                  </p>
                </div>
              </div>;
        })}
        </div>
      </div>
    </section>;
}